/**
 * Team API service for managing company team members and invitations.
 * Uses existing apiRequest utility from utils/apiClient.ts
 */

import { apiRequest } from '../utils/apiClient';
import type { TeamResponse, TeamInvitation, TeamRole } from '../types/company';

/**
 * Fetch team members and usage stats for the current user's company.
 * Endpoint: GET /companies/my/team
 */
export async function getTeam(token: string): Promise<TeamResponse> {
  return apiRequest<TeamResponse>('/companies/my/team', {
    method: 'GET',
    token,
  });
}

/**
 * Invite a new member to the team.
 * Endpoint: POST /companies/my/team/invite
 */
export async function inviteMember(
  token: string,
  email: string,
  role: Exclude<TeamRole, 'OWNER'>
): Promise<{ invitation: TeamInvitation }> {
  return apiRequest<{ invitation: TeamInvitation }>('/companies/my/team/invite', {
    method: 'POST',
    token,
    body: { email, role },
  });
}

/**
 * Fetch pending invitations for the company.
 * Endpoint: GET /companies/my/team/invitations
 */
export async function getPendingInvitations(
  token: string
): Promise<{ invitations: TeamInvitation[] }> {
  return apiRequest<{ invitations: TeamInvitation[] }>('/companies/my/team/invitations', {
    method: 'GET',
    token,
  });
}

/**
 * Remove a member from the team.
 * Endpoint: POST /companies/my/team/:memberId/remove
 *
 * The backend requires a reason (≥10 chars). On success it transfers the
 * removed member's listings to the company owner and emails the removed
 * member a bilingual notice with the reason.
 */
export async function removeMember(
  token: string,
  memberId: string,
  reason: string,
): Promise<{ message: string; transferredCount?: number }> {
  return apiRequest<{ message: string; transferredCount?: number }>(
    `/companies/my/team/${memberId}/remove`,
    {
      method: 'POST',
      body: { reason },
      token,
    },
  );
}

/**
 * Update a team member's role.
 * Endpoint: PUT /companies/my/team/:memberId/role
 */
export async function updateMemberRole(
  token: string,
  memberId: string,
  role: Exclude<TeamRole, 'OWNER'>
): Promise<void> {
  return apiRequest<void>(`/companies/my/team/${memberId}/role`, {
    method: 'PUT',
    token,
    body: { role },
  });
}

/**
 * Cancel a pending invitation.
 * Endpoint: POST /companies/my/team/invitations/:invitationId/cancel
 */
export async function cancelInvitation(token: string, invitationId: string): Promise<void> {
  return apiRequest<void>(`/companies/my/team/invitations/${invitationId}/cancel`, {
    method: 'POST',
    token,
  });
}

/**
 * Resend a pending invitation (generates new token and extends expiration).
 * Endpoint: POST /companies/my/team/invitations/:invitationId/resend
 */
export async function resendInvitation(
  token: string,
  invitationId: string
): Promise<{ invitation: TeamInvitation }> {
  return apiRequest<{ invitation: TeamInvitation }>(
    `/companies/my/team/invitations/${invitationId}/resend`,
    {
      method: 'POST',
      token,
    }
  );
}

/**
 * Invitation details returned by GET /companies/invitation/:token
 */
export interface InvitationDetails {
  id: string;
  companyId: string;
  companyName: string;
  email: string;
  role: TeamRole;
  invitedByName: string;
  expiresAt: string;
  existingUser: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    listingCount: number;
  } | null;
}

/**
 * Get invitation details by token (public endpoint, no auth required).
 * Endpoint: GET /companies/invitation/:token
 */
export async function getInvitationDetails(invitationToken: string): Promise<InvitationDetails> {
  return apiRequest<InvitationDetails>(`/companies/invitation/${invitationToken}`, {
    method: 'GET',
  });
}

/**
 * Accept invitation payload
 */
export interface AcceptInvitationPayload {
  // For new users (creating account)
  password?: string;
  firstName?: string;
  lastName?: string;
  // For existing users with listings
  transferListings?: boolean;
}

/**
 * Accept invitation response
 */
export interface AcceptInvitationResponse {
  message: string;
  isNewUser: boolean;
  listingsTransferred?: number;
  // Only returned for new users (auto-login)
  accessToken?: string;
  refreshToken?: string;
  user?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
  };
}

/**
 * Accept an invitation using a token (public endpoint).
 * For new users: Creates account and joins company
 * For existing users: Joins company and optionally transfers listings
 * Endpoint: POST /companies/invitation/:token/accept
 */
export async function acceptInvitation(
  invitationToken: string,
  payload?: AcceptInvitationPayload,
  authToken?: string
): Promise<AcceptInvitationResponse> {
  return apiRequest<AcceptInvitationResponse>(`/companies/invitation/${invitationToken}/accept`, {
    method: 'POST',
    token: authToken, // Optional - only needed if user is already logged in
    body: payload || {},
  });
}
